<script setup>
import { Head } from '@inertiajs/vue3';
import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { fontsHref, rootClass, rootStyle } from './templates';
import { siteComponentsFor } from './siteComponents';
import { isUnderPrefix, sitePrefix } from './siteLocation.js';
import TemplatePreviewBar from './TemplatePreviewBar.vue';
import SectionDivider from './SectionDivider.vue';
import TrailDetail from './TrailDetail.vue';

const props = defineProps({
    // Title, description, share image and favicon, resolved
    // server-side by SiteMeta so every page type agrees.
    meta: { type: Object, default: () => ({}) },
    org: { type: Object, required: true },
    layout: { type: Object, required: true },
    site_data: { type: Object, default: () => ({}) },
    is_preview: { type: Boolean, default: false },
    preview_reason: { type: String, default: null },
    // Present when rendering a single news post — the post slot
    // blocks (PostHero / PostTitle / PostMeta / PostBody / PostExcerpt)
    // read this via inject('tiqCurrentPost').
    current_post: { type: Object, default: null },
    // Same idea for a single event — read by the event slot blocks
    // via inject('tiqCurrentEvent').
    current_event: { type: Object, default: null },
    // A trail's own page (SiteTrail) — read by the trail_detail block.
    current_trail: { type: Object, default: null },
    // A network's own page (SiteNetwork) — read by the network_* blocks.
    current_network: { type: Object, default: null },
});

// Expose the components registry so nested blocks (e.g. ColumnsBlock)
// can look up child types without re-importing blocks.js — that
// would be a circular dependency.
// The chosen template's renderers. A template is a design only, so
// every block below reads the same config whichever one is picked.
//
// `trail_detail` is added here rather than to the templates' registries:
// it is placed only by the server, on a trail's own page, and is never
// stored, so it is not a block type a manager can add or save.
const COMPONENTS = { ...siteComponentsFor(props.layout.theme), trail_detail: TrailDetail };
provide('tiqBlockComponents', COMPONENTS);
// When rendering a single post, post slot blocks inject this.
provide('tiqCurrentPost', props.current_post);
provide('tiqCurrentEvent', props.current_event);
provide('tiqCurrentTrail', props.current_trail);
provide('tiqCurrentNetwork', props.current_network);

const blocks = computed(() => (props.layout.blocks || []).filter((b) => COMPONENTS[b.type]));
const accent = computed(() => props.layout.theme?.accent || '#dd9b36');

// An event page in the Trailhead family puts its sign-up card BESIDE
// the details on a wide screen. The card is that design's aside, and
// blocks are stacked siblings, so on its own it sat under the details
// at 420px and left most of a desktop screen empty beside a long form.
// The details that come straight before the sign-up (when, where, the
// write-up, the work) are grouped with it into one row; anything else
// a manager has placed between them breaks the run and the page stays
// a stack, so no block ever moves past one it was placed after. The
// row only exists above a width (see .tiq-event-split), so a phone is
// unchanged.
const EVENT_DETAIL_TYPES = new Set(['event_title', 'event_when', 'event_location', 'event_excerpt', 'event_body', 'event_maintenance']);
const segments = computed(() => {
    const list = blocks.value;
    const whole = [{ kind: 'stack', blocks: list }];
    if (!props.current_event || !rootClass(props.layout.theme).includes('tpl-trailhead')) return whole;
    const at = list.findIndex((b) => b.type === 'event_signup');
    if (at < 1) return whole;
    let from = at;
    while (from > 0 && EVENT_DETAIL_TYPES.has(list[from - 1].type)) from--;
    if (from === at) return whole;
    return [
        { kind: 'stack', blocks: list.slice(0, from) },
        { kind: 'split', blocks: list.slice(from, at), aside: list[at] },
        { kind: 'stack', blocks: list.slice(at + 1) },
    ];
});

// When the site is being rendered via the canonical preview URL
// (/site/{slug}/...) instead of a vanity domain, every internal link
// authored as "/conditions" needs to be rewritten to
// "/site/{slug}/conditions" — otherwise it 404s to the app's root.
// Done as a delegated click handler rather than rewriting each
// block's hrefs server-side: catches every <a>, regardless of which
// block emits it.
const basePath = ref('');
function detectBase() {
    basePath.value = sitePrefix();
}
function onClick(e) {
    if (!basePath.value) return;
    const a = e.target.closest && e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href || !href.startsWith('/') || href.startsWith('//')) return;
    // Already on the site's prefix. Ask about the PATH, not the string:
    // the template bar's Try links are "/site/{slug}?template=…", which
    // neither equals the prefix nor starts with "{prefix}/", and were
    // prefixed a second time into a 404.
    if (isUnderPrefix(href, basePath.value)) return;
    // Modifier-key clicks (open in new tab/window) shouldn't be intercepted —
    // let the browser do its native thing with the unrewritten href.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    window.location.href = basePath.value + href;
}
onMounted(() => {
    detectBase();
    document.addEventListener('click', onClick);
});
onBeforeUnmount(() => document.removeEventListener('click', onClick));
</script>

<template>
    <Head>
        <title>{{ meta?.title || org.name }}</title>
        <!-- Description, canonical, favicon and the Open Graph /
             Twitter tags are rendered by the shell (app.blade.php)
             from `meta`, where a link preview can read them. -->
        <link rel="preconnect" href="https://fonts.googleapis.com">
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
        <link rel="stylesheet" :href="fontsHref(layout.theme)">
    </Head>
    <div class="tiq-site" :class="[rootClass(layout.theme), { 'has-preview-banner': is_preview }]" :style="rootStyle(layout.theme)">
        <TemplatePreviewBar :theme="layout.theme" />
        <div v-if="is_preview" class="tiq-preview-banner">
            <span>{{ preview_reason || 'Preview — this site is currently hidden from visitors.' }}</span>
        </div>
        <template v-for="(seg, si) in segments" :key="si">
            <div v-if="seg.kind === 'split'" class="tiq-event-split"><div class="tiq-event-split__inner">
                <div class="tiq-event-split__main">
                    <template v-for="block in seg.blocks" :key="block.id">
                        <SectionDivider v-if="block.config?.divider_top?.shape && block.config.divider_top.shape !== 'none'"
                                        :spec="block.config.divider_top" />
                        <component :is="COMPONENTS[block.type]" :config="block.config" :accent="accent" :org="org" :site_data="site_data" />
                        <SectionDivider v-if="block.config?.divider_bottom?.shape && block.config.divider_bottom.shape !== 'none'"
                                        :spec="block.config.divider_bottom" />
                    </template>
                </div>
                <div class="tiq-event-split__aside">
                    <component :is="COMPONENTS[seg.aside.type]" :config="seg.aside.config" :accent="accent" :org="org" :site_data="site_data" />
                </div>
            </div></div>
            <template v-else><template v-for="block in seg.blocks" :key="block.id">
                <SectionDivider v-if="block.config?.divider_top?.shape && block.config.divider_top.shape !== 'none'"
                                :spec="block.config.divider_top" />
                <component
                    :is="COMPONENTS[block.type]"
                    :config="block.config"
                    :accent="accent"
                    :org="org"
                    :site_data="site_data"
                />
                <SectionDivider v-if="block.config?.divider_bottom?.shape && block.config.divider_bottom.shape !== 'none'"
                                :spec="block.config.divider_bottom" />
            </template></template>
        </template>
        <div v-if="blocks.length === 0" class="tiq-site-empty">
            <h1>{{ org.name }}</h1>
            <p>This website is still being built.</p>
        </div>
    </div>
</template>

<style>
/* Global resets/styles for the public site — scoped: false so they
   reach inside block components without us repeating them. */
.tiq-site {
    font-family: 'Barlow', sans-serif;
    color: #1a1a1a;
    background: #f8f8f8;
    min-height: 100vh;
}
.tiq-site * { box-sizing: border-box; }

.tiq-site-empty {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 40px 24px;
    background: #1a1a1a;
    color: #fff;
}
.tiq-site-empty h1 {
    font-family: 'Bebas Neue', sans-serif;
    font-weight: bold;
    font-size: clamp(48px, 10vw, 96px);
    letter-spacing: 0.04em;
    margin: 0 0 12px;
}
.tiq-preview-banner {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 9999;
    background: #fde68a;
    color: #78350f;
    text-align: center;
    padding: 8px 16px;
    font-family: 'Barlow Condensed', sans-serif;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    font-size: 12px;
    font-weight: 600;
    height: 32px;
}
/* Push everything below the fixed banner so the MenuBlock's fixed
   nav doesn't end up behind it. `!important` overrides MenuBlock's
   own scoped `top: 0` (scoped CSS adds attribute selectors that
   tie source-order specificity, so we force the win here). */
.tiq-site.has-preview-banner { padding-top: 32px; }
.tiq-site.has-preview-banner .tiq-nav { top: 32px !important; }
.tiq-site-empty p {
    font-family: 'Barlow', sans-serif;
    color: rgba(255,255,255,0.7);
    margin: 0;
}

/* The event page's details beside its sign-up card (segments in the
   script). The outer box is one block-level sibling at every width, so
   a template that draws something between neighbouring blocks (Backyard's
   waves) sees one ordinary block where the run was; below the width the
   inner boxes are plain blocks and the page reads as the stack did. */
.tiq-event-split { display: block; }
@media (min-width: 1100px) {
    .tiq-site.tpl-trailhead .tiq-event-split__inner {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 420px;
        column-gap: clamp(32px, 4vw, 64px);
        align-items: start;
        max-width: 1240px;
        margin: 0 auto;
        padding-left: var(--th-gutter);
        padding-right: var(--th-gutter);
    }
    .tiq-site.tpl-trailhead .tiq-event-split__main { display: block; min-width: 0; }
    .tiq-site.tpl-trailhead .tiq-event-split__aside {
        display: block;
        position: sticky;
        /* Clear the sticky site header. */
        top: 88px;
    }
    .tiq-site.tpl-trailhead .tiq-event-split .th-wrap {
        max-width: none;
        margin: 0;
        padding-left: 0;
        padding-right: 0;
    }
}
</style>
